import { useState, useEffect, useCallback, useRef } from 'react';
import { ChatMessage, ChatAttachment } from '../types';
import { chatService } from '../services/chatService';
import { useAuth } from '../context/AuthContext';

export const useChat = (deckId: string, slideNumber?: number | string) => {
  const { token } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [newMessageId, setNewMessageId] = useState<string | null>(null);
  const [pollingRequestId, setPollingRequestId] = useState<string | null>(null);
  const [pollCount, setPollCount] = useState(0);
  const [isTypewriting, setIsTypewriting] = useState(false);
  const [lastContentLength, setLastContentLength] = useState(0);
  const [typingContent, setTypingContent] = useState('');
  const [typingMessageId, setTypingMessageId] = useState<string | null>(null);
  const typewriterIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isCompletingRef = useRef<boolean>(false); // Prevents auto-reload during completion

  const loadHistory = useCallback(async () => {
    if (!token || !deckId) return;

    console.log('[useChat] loadHistory called - slideNumber:', slideNumber, 'deckId:', deckId);
    
    setLoading(true);
    setError(null);
    setNewMessageId(null); // Clear new message flag when loading history

    try {
      // Use a higher limit to ensure we get all messages, even if conversation has grown
      const history = await chatService.getChatHistory(deckId, token, 100, undefined, slideNumber);
      console.log('[useChat] Loaded history:', history.messages.length, 'messages for slideNumber:', slideNumber);
      setMessages(history.messages);
    } catch (err) {
      console.error('[useChat] Error loading history:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [deckId, token, slideNumber]);

  const sendMessage = useCallback(async (
    content: string,
    attachments: ChatAttachment[] = []
  ) => {
    console.log('[useChat] sendMessage called with:', { content, deckId, slideNumber });
    
    if (!token || !deckId) {
      console.log('[useChat] sendMessage aborted - no token or deckId');
      return;
    }

    setSending(true);
    setError(null);

    // Add user message immediately (optimistic update)
    const userMessage: ChatMessage = {
      messageId: `temp_user_${Date.now()}`,
      role: 'user',
      content,
      timestamp: new Date().toISOString(),
      attachments: attachments,
    };

    console.log('[useChat] Adding optimistic user message:', userMessage.messageId);
    setMessages(prev => {
      console.log('[useChat] Previous messages:', prev.length);
      return [...prev, userMessage];
    });

    try {
      // Initiate async chat request
      const { requestId } = await chatService.initiateChat(deckId, content, attachments, token, slideNumber);
      setPollingRequestId(requestId);
      setPollCount(0);
      setLastContentLength(0);
      
      // Start polling for response
      const maxPolls = 300; // Max 5 minutes with variable polling
      let pollAttempt = 0;
      let completed = false;
      let currentDisplayedContent = '';
      let fullResponseContent = '';
      
      const aiMessageId = `ai_${requestId}`;
      const userMessageId = `user_${requestId}`;
      
      // Function to display content with typewriter effect
      const typewriteNewContent = (newChunk: string, timestamp: string, isResponseComplete: boolean) => {
        if (newChunk.length === 0) {
          // No new content, continue polling
          if (!isResponseComplete && pollAttempt < maxPolls) {
            const delay = pollAttempt < 10 ? 1000 : 2000;
            pollTimeoutRef.current = setTimeout(poll, delay);
          } else if (isResponseComplete) {
            handleComplete(timestamp);
          }
          return;
        }
        
        setIsTypewriting(true);
        setTypingMessageId(aiMessageId);
        let charIndex = 0;
        
        // Add initial message with user content and AI content at current state
        setMessages(prev => {
          const filtered = prev.filter(m => 
            m.messageId !== userMessage.messageId && 
            m.messageId !== aiMessageId &&
            m.messageId !== userMessageId
          );
          
          return [
            ...filtered,
            {
              messageId: userMessageId,
              role: 'user' as const,
              content,
              timestamp,
            },
            {
              messageId: aiMessageId,
              role: 'assistant' as const,
              content: currentDisplayedContent,
              timestamp,
            },
          ];
        });
        
        const typewriterStep = () => {
          if (charIndex < newChunk.length) {
            // Add multiple characters per frame for better performance (10 chars per 10ms = 1000 chars/sec)
            const charsToAdd = Math.min(10, newChunk.length - charIndex);
            charIndex += charsToAdd;
            const newDisplayedContent = currentDisplayedContent + newChunk.slice(0, charIndex);
            
            // Update typing content for immediate display
            setTypingContent(newDisplayedContent);
            
            // Update the full messages array at markdown-safe boundaries (after newlines or every 100 chars)
            // This ensures markdown is parsed on complete content
            const lastChar = newChunk[charIndex - 1];
            const shouldUpdateMessage = lastChar === '\n' || charIndex % 100 === 0;
            
            if (shouldUpdateMessage) {
              setMessages(prev => {
                const filtered = prev.filter(m => m.messageId !== aiMessageId);
                return [
                  ...filtered,
                  {
                    messageId: aiMessageId,
                    role: 'assistant' as const,
                    content: newDisplayedContent,
                    timestamp,
                  },
                ];
              });
            }
          } else {
            // Typewriter complete for this chunk
            if (typewriterIntervalRef.current) {
              clearInterval(typewriterIntervalRef.current);
              typewriterIntervalRef.current = null;
            }
            setIsTypewriting(false);
            setTypingMessageId(null);
            
            // Update the current displayed content
            currentDisplayedContent += newChunk;
            setLastContentLength(currentDisplayedContent.length);
            setTypingContent('');
            
            // Final update to messages array with complete chunk
            setMessages(prev => {
              const filtered = prev.filter(m => m.messageId !== aiMessageId);
              return [
                ...filtered,
                {
                  messageId: aiMessageId,
                  role: 'assistant' as const,
                  content: currentDisplayedContent,
                  timestamp,
                },
              ];
            });
            
            // Continue polling if not complete
            if (!isResponseComplete && pollAttempt < maxPolls) {
              const delay = pollAttempt < 10 ? 1000 : 2000;
              pollTimeoutRef.current = setTimeout(poll, delay);
            } else if (isResponseComplete) {
              handleComplete(timestamp);
            }
          }
        };
        
        // Start typewriter effect (10 characters every 10ms = 1000 chars/sec, same speed as 1 char/1ms but 10x fewer updates)
        typewriterIntervalRef.current = setInterval(typewriterStep, 10);
      };
      
      const handleComplete = (timestamp: string) => {
        console.log('[useChat] handleComplete called');
        completed = true;
        isCompletingRef.current = true; // Prevent auto-reload from useEffect during save
        setPollingRequestId(null);
        setSending(false);
        setIsTypewriting(false);
        setLastContentLength(0);
        setTypingContent('');
        setTypingMessageId(null);
        
        // Capture the current message count BEFORE we start polling
        // We expect to have at least this many messages (including optimistic ones)
        setMessages(currentMessages => {
          const expectedCount = currentMessages.length;
          console.log(`[useChat] Expected message count: ${expectedCount}`);
          
          // Poll history until we see persisted messages (with real ULID IDs, not temp IDs)
          // This ensures we get the messages from DynamoDB once the async worker has saved them
          const maxPollAttempts = 10; // Poll up to 10 times (5 seconds total)
          let pollAttempt = 0;
          
          const pollForPersistedMessages = async () => {
            if (pollAttempt >= maxPollAttempts) {
              console.log('[useChat] Max poll attempts reached, stopping');
              isCompletingRef.current = false;
              return;
            }
            
            pollAttempt++;
            console.log(`[useChat] Polling for persisted messages (attempt ${pollAttempt}/${maxPollAttempts})`);
            
            try {
              const history = await chatService.getChatHistory(deckId, token!, 100, undefined, slideNumber);
              
              console.log(`[useChat] Poll result: ${history.messages.length} messages, slideNumber: ${slideNumber}, expected: ${expectedCount}`);
              
              // Check if the most recent messages have real ULID IDs (not temp IDs)
              // Real ULIDs are 26 characters and start with timestamp (0-9, A-Z)
              const recentMessages = history.messages.slice(-2);
              const allRecentHaveRealIds = recentMessages.length > 0 && recentMessages.every(msg => {
                const id = msg.messageId;
                return id && id.length === 26 && /^[0-9A-Z]/.test(id);
              });
              
              // Only update if:
              // 1. History has AT LEAST as many messages as we expect (including new ones)
              // 2. The recent messages have real ULID IDs (meaning they're persisted)
              const hasEnoughMessages = history.messages.length >= expectedCount;
              
              console.log(`[useChat] Poll check: hasEnough=${hasEnoughMessages}, recentReal=${allRecentHaveRealIds}, history=${history.messages.length}, expected=${expectedCount}`);
              
              if (hasEnoughMessages && allRecentHaveRealIds) {
                console.log('[useChat] Found persisted messages with real IDs, updating state');
                setMessages(history.messages);
                isCompletingRef.current = false;
              } else {
                console.log(`[useChat] Messages not yet persisted (need ${expectedCount}, have ${history.messages.length}), continuing to poll`);
                // Keep polling
                setTimeout(pollForPersistedMessages, 500);
              }
            } catch (err) {
              console.error('[useChat] Error polling for persisted messages:', err);
              // Retry on error
              setTimeout(pollForPersistedMessages, 500);
            }
          };
          
          // Start polling after a short delay to give the async worker a head start
          setTimeout(pollForPersistedMessages, 500);
          
          // Return current messages unchanged (we'll update via setMessages in the poll callback)
          return currentMessages;
        });
      };
      
      const poll = async () => {
        if (!token || completed) return;
        
        pollAttempt++;
        setPollCount(pollAttempt);
        
        try {
          const result = await chatService.getChatResponse(requestId, token);
          const timestamp = result.timestamp || new Date().toISOString();
          
          // Check if we have new content
          if (result.content && result.content.length > 0) {
            fullResponseContent = result.content;
            
            // Calculate the new chunk (content that wasn't displayed yet)
            const newChunk = result.content.slice(currentDisplayedContent.length);
            
            if (result.status === 'completed') {
              // Display any remaining content and mark as complete
              typewriteNewContent(newChunk, timestamp, true);
            } else if (result.status === 'processing') {
              // Display the new chunk with typewriter effect, then poll again
              typewriteNewContent(newChunk, timestamp, false);
            }
          } else if (result.status === 'processing') {
            // No content yet, continue polling
            if (pollAttempt < maxPolls) {
              const delay = pollAttempt < 10 ? 1000 : 2000;
              pollTimeoutRef.current = setTimeout(poll, delay);
            } else {
              // Max polls reached
              setPollingRequestId(null);
              setError(new Error('Response timeout - please try again'));
              setMessages(prev => prev.filter(m => m.messageId !== userMessage.messageId));
              setSending(false);
            }
          } else if (result.status === 'completed') {
            // Completed but no content
            handleComplete(timestamp);
          } else if (result.status === 'error') {
            completed = true;
            setPollingRequestId(null);
            setError(new Error(result.errorMessage || 'Chat processing failed'));
            
            // If no content was received, remove temp messages
            if (!result.content || result.content.length === 0) {
              setMessages(prev => prev.filter(m => m.messageId !== userMessage.messageId));
            }
            setSending(false);
            setIsTypewriting(false);
            setTypingContent('');
            setTypingMessageId(null);
          }
        } catch (err) {
          console.error('[useChat] Polling error:', err);
          if (pollAttempt < maxPolls && !completed) {
            // Retry polling on error
            pollTimeoutRef.current = setTimeout(poll, 5000);
          } else {
            setPollingRequestId(null);
            setError(err as Error);
            setMessages(prev => prev.filter(m => m.messageId !== userMessage.messageId));
            setSending(false);
            setIsTypewriting(false);
            setTypingContent('');
            setTypingMessageId(null);
          }
        }
      };
      
      // Start initial poll after 1 second
      pollTimeoutRef.current = setTimeout(poll, 1000);
      
    } catch (err) {
      console.error('[useChat] Initiate chat error:', err);
      setError(err as Error);
      setMessages(prev => prev.filter(m => m.messageId !== userMessage.messageId));
      setNewMessageId(null);
      setSending(false);
    }
  }, [deckId, token, slideNumber, loadHistory]);

  // Keep uploadAttachment for backward compatibility (deprecated)
  const uploadAttachment = useCallback(async (
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<string> => {
    if (!token || !deckId) throw new Error('Not authenticated');

    // Get presigned URL
    const { uploadUrl, s3Key } = await chatService.getAttachmentPresignedUrl(
      deckId,
      file.name,
      file.type,
      file.size,
      token
    );

    // Upload to S3
    await chatService.uploadAttachment(uploadUrl, file, onProgress);

    return s3Key;
  }, [deckId, token]);

  useEffect(() => {
    // Don't auto-reload history if we're actively polling/sending or in completion phase
    // Also don't load if slideNumber is undefined (wait for it to be set)
    // (handleComplete will reload after a delay to get the final messages)
    if (!pollingRequestId && !sending && !isCompletingRef.current && slideNumber !== undefined) {
      console.log('[useChat] useEffect triggering loadHistory - slideNumber:', slideNumber);
      loadHistory();
    } else {
      console.log('[useChat] useEffect skipping loadHistory:', {
        pollingRequestId: !!pollingRequestId,
        sending,
        isCompletingRef: isCompletingRef.current,
        slideNumber
      });
    }
  }, [loadHistory, pollingRequestId, sending, slideNumber]);

  // Reset isCompletingRef when slide number changes to allow loading new slide's history
  useEffect(() => {
    console.log('[useChat] slideNumber changed, resetting isCompletingRef:', slideNumber);
    isCompletingRef.current = false;
  }, [slideNumber]);

  // Cleanup polling and typewriter on unmount
  useEffect(() => {
    return () => {
      if (pollingRequestId) {
        // Clear any active intervals or timeouts
        if (typewriterIntervalRef.current) {
          clearInterval(typewriterIntervalRef.current);
          typewriterIntervalRef.current = null;
        }
        if (pollTimeoutRef.current) {
          clearTimeout(pollTimeoutRef.current);
          pollTimeoutRef.current = null;
        }
        
        setPollingRequestId(null);
        setIsTypewriting(false);
        setTypingContent('');
        setTypingMessageId(null);
      }
    };
  }, [pollingRequestId]);

  return { 
    messages, 
    loading, 
    sending, 
    error, 
    sendMessage, 
    uploadAttachment, 
    refresh: loadHistory, 
    newMessageId,
    typingContent,
    typingMessageId
  };
};

